import { GridColDef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { addItemsBaseColumnProps } from './useAddItemsBaseColumns';
import { dataGridBoldClass } from '@shared/styles';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { AddCampaignItemsSchema, BaseCampaignItemSchema, CellSelectInputField } from '..';
import { Controller, useFormContext } from 'react-hook-form';
import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';
import { Tooltip } from '@mui/material';

export const useUnitColumns = () => {
  const t = useCampaignTFunction();

  const {
    control,
    formState: { errors },
  } = useFormContext<AddCampaignItemsSchema<BaseCampaignItemSchema>>();

  return useMemo(() => {
    const getPrimaryPackageUnitColumn = (): GridColDef<BaseMgSupplierCampaignItem> => ({
      ...addItemsBaseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 160,
      headerAlign: 'center',
      align: 'center',
      field: 'primaryPackageUnit',
      headerName: t('campaign.columns.primaryPackageUnit'),
      renderCell: ({ row }) => {
        const primaryUnit = row.getPrimaryPackageUnit();
        return primaryUnit ? (
          <Tooltip title={primaryUnit.description}>
            <span>{primaryUnit.unit}</span>
          </Tooltip>
        ) : (
          '-'
        );
      },
    });

    const getCampaignUnitColumn = (): GridColDef<BaseMgSupplierCampaignItem> => ({
      ...addItemsBaseColumnProps,
      width: 200,
      headerAlign: 'center',
      align: 'center',
      field: 'campaignUnit',
      headerName: t('campaign.columns.campaignUnit.label'),
      renderCell: ({ row }) => {
        const unitOptions =
          row.packages.map(({ unit }) => ({
            value: unit.unit,
            name: unit.getFormattedUnitText(),
          })) ?? [];

        return (
          <Controller
            name={`items.${row.mgSupplierItemId}.campaignUnit`}
            control={control}
            render={({ field }) => (
              <CellSelectInputField
                {...field}
                fullWidth
                size="small"
                menuItems={unitOptions}
                error={
                  errors.items?.[row.mgSupplierItemId]?.campaignUnit
                    ? errors.items?.[row.mgSupplierItemId]?.campaignUnit?.message
                    : ''
                }
                required
              />
            )}
          />
        );
      },
    });

    return { getPrimaryPackageUnitColumn, getCampaignUnitColumn };
  }, [control, errors.items, t]);
};
